import { useState, useEffect, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import colors from '../utils/style/colors'

// Bumper cette clé pour réafficher le bandeau à tous ceux qui l'ont fermé
const DISMISS_KEY = 'pf_banner_secheresse_2026'

// Pages internes / outils : pas de bandeau
const HIDDEN_PATHS = ['/admin', '/devis-generator', '/suivi-devis']

const MESSAGE = "En raison de la sécheresse 2026 sur les fourrages et d'une forte demande, les livraisons sont retardées, cela concerne uniquement le Foin et la Luzerne. Les demandes de devis actuelles après validation vous permettent d'être inscrit sur liste d'attente avec livraison à partir de la mi/fin-septembre."

const drift = keyframes`
  0%   { background-position:   0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position:   0% 50%; }
`
const shimmer = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`
const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.72; }
`

const Band = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 300;
  overflow: hidden;
  background: linear-gradient(
    100deg,
    #8f1d07 0%,
    #c0390f 22%,
    #e0731a 44%,
    #c9531a 66%,
    #a3260a 85%,
    #8f1d07 100%
  );
  background-size: 300% 100%;
  animation: ${drift} 14s ease-in-out infinite, ${slideDown} 0.45s ease both;
  border-bottom: 1px solid rgba(200,168,75,0.4);
  box-shadow: 0 2px 18px rgba(0,0,0,0.3);
  @media (prefers-reduced-motion: reduce) {
    animation: ${slideDown} 0.45s ease both;
  }
`
const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.07;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px 200px;
`
/* Voile clair qui balaye lentement le bandeau */
const Sheen = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(105deg, transparent 35%, rgba(255,240,200,0.16) 50%, transparent 65%);
  animation: ${shimmer} 7s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`
/* Liseré doré en bas, comme les séparateurs du site */
const GoldBar = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, ${colors.orClair}, transparent);
`
const Alert = styled.span`
  flex-shrink: 0;
  font-size: 1rem;
  line-height: 1;
  animation: ${pulse} 3.4s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`

const Cta = styled(Link)`
  flex-shrink: 0;
  background: #fff;
  color: #9c2408;
  font-family: ${colors.font};
  font-size: 0.79rem;
  font-weight: 600;
  letter-spacing: 0.015em;
  text-decoration: none;
  white-space: nowrap;
  padding: 9px 19px;
  border-radius: 4px;
  transition: all 0.22s;
  &:hover {
    background: ${colors.orClair};
    color: ${colors.texte};
    transform: translateY(-1px);
    box-shadow: 0 5px 18px rgba(0,0,0,0.28);
  }
`
const Close = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  color: rgba(255,255,255,0.62);
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  padding: 4px 2px;
  transition: color 0.2s;
  &:hover { color: #fff; }
`

/* ── Desktop ── */
const DeskInner = styled.div`
  position: relative;
  max-width: 1240px;
  margin: 0 auto;
  padding: 11px 22px;
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 900px) { display: none; }
`
const DeskText = styled.p`
  margin: 0;
  flex: 1;
  font-family: ${colors.font};
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.65;
  letter-spacing: 0.008em;
  color: rgba(255,255,255,0.96);
`

/* ── Mobile ── */
const MobInner = styled.div`
  position: relative;
  display: none;
  @media (max-width: 900px) { display: block; }
`
const MobBar = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 10px 12px 10px 14px;
  display: flex;
  /* Ouvert, le texte passe sur plusieurs lignes : icône et chevron restent en haut */
  align-items: ${(p) => (p.$open ? 'flex-start' : 'center')};
  gap: 9px;
  cursor: pointer;
  text-align: left;
  font-family: ${colors.font};
`
const MobLabel = styled.span`
  flex: 1;
  min-width: 0;
  color: rgba(255,255,255,0.96);
  font-size: 0.79rem;
  font-weight: 400;
  letter-spacing: 0.008em;
  line-height: ${(p) => (p.$open ? '1.7' : '1.35')};
  /* Fermé : une seule ligne tronquée. Ouvert : le message complet. */
  white-space: ${(p) => (p.$open ? 'normal' : 'nowrap')};
  overflow: ${(p) => (p.$open ? 'visible' : 'hidden')};
  text-overflow: ellipsis;
`
const Chevron = styled.span`
  flex-shrink: 0;
  color: #fff;
  font-size: 0.6rem;
  transition: transform 0.28s ease;
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
`
const MobPanel = styled.div`
  position: relative;
  padding: 0 14px 13px;
  animation: ${slideDown} 0.28s ease both;
`
const MobActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`
const MobCta = styled(Cta)`
  flex: 1;
  text-align: center;
  padding: 11px 14px;
  font-size: 0.73rem;
`
const MobDismiss = styled.button`
  flex-shrink: 0;
  background: none;
  border: 1px solid rgba(255,255,255,0.35);
  color: rgba(255,255,255,0.85);
  font-family: ${colors.font};
  font-size: 0.75rem;
  font-weight: 400;
  padding: 11px 14px;
  border-radius: 4px;
  cursor: pointer;
`

export default function BannerFourrage() {
  const { pathname } = useLocation()
  const [visible, setVisible] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) !== '1' } catch { return true }
  })
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const hidden = !visible || HIDDEN_PATHS.includes(pathname)

  // Le bandeau est en position fixed : on réserve sa hauteur réelle via une
  // variable CSS, que la nav et le body utilisent pour se décaler.
  useEffect(() => {
    const root = document.documentElement
    if (hidden) {
      root.style.setProperty('--pf-banner-h', '0px')
      return
    }
    const el = ref.current
    if (!el) return
    const sync = () => root.style.setProperty('--pf-banner-h', `${el.offsetHeight}px`)
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    return () => { ro.disconnect(); root.style.setProperty('--pf-banner-h', '0px') }
  }, [hidden, open])

  if (hidden) return null

  const dismiss = () => {
    setVisible(false)
    try { localStorage.setItem(DISMISS_KEY, '1') } catch { /* navigation privée */ }
  }

  return (
    <Band ref={ref} role="region" aria-label="Information sur les délais de livraison">
      <Grain />
      <Sheen />

      <DeskInner>
        <Alert aria-hidden="true">⚠️</Alert>
        <DeskText>{MESSAGE}</DeskText>
        <Cta to="/devis">Demander un devis</Cta>
        <Close onClick={dismiss} aria-label="Fermer le bandeau">×</Close>
      </DeskInner>

      <MobInner>
        <MobBar $open={open} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <Alert aria-hidden="true">⚠️</Alert>
          <MobLabel $open={open}>{MESSAGE}</MobLabel>
          <Chevron $open={open} aria-hidden="true">▼</Chevron>
        </MobBar>
        {open && (
          <MobPanel>
            <MobActions>
              <MobCta to="/devis">Demander un devis</MobCta>
              <MobDismiss onClick={dismiss}>Masquer</MobDismiss>
            </MobActions>
          </MobPanel>
        )}
      </MobInner>

      <GoldBar />
    </Band>
  )
}
